import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatDialogRef } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import { ButtonComponent } from '../../../components/button/button.component'
import { DateInputComponent } from '../../../components/date-input/date-input.component'
import { dateRangeValidator } from '../../../directives/validator/custom-validator'
import { SkillMap } from '../../../services/assessment/assessment.mapping'
import { AssessmentStore } from '../../../stores/assessment.store'

export type ExportIntegratedAssessmentDialogResult = {
  startDate: Date | null
  endDate: Date | null
  skillMapIds: string[]
}

/** サーバー側 (backend/src/routes/integratedExportHandler.ts) と同値の期間上限。超過すると 400 になる */
const MAX_DATE_RANGE_DAYS = 366

/** 日付をその日の 00:00:00.000 に正規化する */
function startOfDay(date: Date): Date {
  const normalized = new Date(date)
  normalized.setHours(0, 0, 0, 0)
  return normalized
}

/** 日付をその日の 23:59:59.999 に正規化する */
function endOfDay(date: Date): Date {
  const normalized = new Date(date)
  normalized.setHours(23, 59, 59, 999)
  return normalized
}

/**
 * 期間がサーバーの上限を超えていないか検証する。
 * 判定式・比較対象はサーバーと完全に同一にする（実際に送信する正規化済みの値で
 * Math.floor((end - start) / 1日) > 上限 で判定する）。暦日カウントで実装すると
 * クライアントだけが厳しくなり、サーバーが受け付ける範囲を弾いてしまう。
 */
const maxDateRangeValidator: ValidatorFn = (
  group: AbstractControl,
): ValidationErrors | null => {
  const start: Date | null = group.get('startDate')?.value
  const end: Date | null = group.get('endDate')?.value

  if (!start || !end) {
    return null
  }

  const daysDiff = Math.floor(
    (endOfDay(end).getTime() - startOfDay(start).getTime()) /
      (1000 * 60 * 60 * 24),
  )

  return daysDiff > MAX_DATE_RANGE_DAYS ? { dateRangeTooLong: true } : null
}

@UntilDestroy()
@Component({
  selector: 'app-export-integrated-assessment',
  templateUrl: './export-integrated-assessment.component.html',
  styleUrls: ['./export-integrated-assessment.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatIcon,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    DateInputComponent,
    ButtonComponent,
  ],
})
export class ExportIntegratedAssessmentComponent implements OnInit {
  startDate = new FormControl<Date | null>(null)
  endDate = new FormControl<Date | null>(null)

  formGroup = this.formBuilder.group({
    startDate: this.startDate,
    endDate: this.endDate,
  })

  loading = true
  skillMaps: SkillMap[] = []
  selectedSkillMapIds: string[] = []
  readonly maxDateRangeDays = MAX_DATE_RANGE_DAYS

  constructor(
    private matDialogRef: MatDialogRef<ExportIntegratedAssessmentComponent>,
    private formBuilder: FormBuilder,
    private assessmentStore: AssessmentStore,
  ) {}

  ngOnInit(): void {
    const today = new Date()
    this.endDate.setValue(today)
    const DEFAULT_PERIOD_DAYS = 7
    const pastDay = new Date(today)
    pastDay.setDate(pastDay.getDate() - DEFAULT_PERIOD_DAYS)
    this.startDate.setValue(pastDay)

    this.formGroup.setValidators([
      dateRangeValidator('startDate', 'endDate'),
      maxDateRangeValidator,
    ])

    this.assessmentStore
      .getSkillMaps()
      .pipe(untilDestroyed(this))
      .subscribe((skillMaps) => {
        this.skillMaps = skillMaps
        this.loading = false
      })
  }

  /** 全スキルマップが選択されているか */
  get isAllSelected(): boolean {
    return (
      this.skillMaps.length > 0 &&
      this.selectedSkillMapIds.length === this.skillMaps.length
    )
  }

  /** 一部のスキルマップが選択されているか */
  get isIndeterminate(): boolean {
    return (
      this.selectedSkillMapIds.length > 0 &&
      this.selectedSkillMapIds.length < this.skillMaps.length
    )
  }

  /** フォームが有効か（スキルマップ選択済み、日付バリデーション通過） */
  get isFormValid(): boolean {
    return (
      this.selectedSkillMapIds.length > 0 &&
      !this.formGroup.hasError('dateRangeInvalid') &&
      !this.formGroup.hasError('dateRangeTooLong')
    )
  }

  /**
   * スキルマップの KA 総数を返す
   * @param skillMap 対象スキルマップ
   */
  getKeyActionCount(skillMap: SkillMap): number {
    return skillMap.phases.reduce(
      (sum, phase) => sum + phase.keyActions.length,
      0,
    )
  }

  /**
   * スキルマップの選択状態をトグルする
   * @param skillMapId トグル対象のスキルマップID
   */
  toggleSkillMap(skillMapId: string): void {
    const index = this.selectedSkillMapIds.indexOf(skillMapId)
    if (index >= 0) {
      this.selectedSkillMapIds = this.selectedSkillMapIds.filter(
        (id) => id !== skillMapId,
      )
    } else {
      this.selectedSkillMapIds = [...this.selectedSkillMapIds, skillMapId]
    }
  }

  /**
   * 全スキルマップを選択または全解除する
   * @param checked true で全選択、false で全解除
   */
  toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.selectedSkillMapIds = this.skillMaps.map((sm) => sm.id)
    } else {
      this.selectedSkillMapIds = []
    }
  }

  /** エクスポートを実行しダイアログを閉じる */
  submit(): void {
    if (!this.isFormValid) {
      return
    }

    const startDateVal = this.startDate.value
    const endDateVal = this.endDate.value
    const result: ExportIntegratedAssessmentDialogResult = {
      startDate: startDateVal ? startOfDay(startDateVal) : null,
      endDate: endDateVal ? endOfDay(endDateVal) : null,
      skillMapIds: [...this.selectedSkillMapIds],
    }
    this.matDialogRef.close(result)
  }

  /** ダイアログを閉じる */
  close(): void {
    this.matDialogRef.close()
  }
}
